import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { createSelector } from 'reselect'
import FieldPosition from './FieldPosition'
import Icon from '../../../misc/Icon'
import InputSettingsField from '../../settings/InputSettingsField'
import FieldTypes from '../../../../constants/FieldTypes'

import ThemeIcons from '../../../../theme/Icons'

const StyledSearchableFieldListWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	overflow-y: auto;

	::-webkit-scrollbar {
		display: none; //hide scrollbar in chrome
	}
	scrollbar-width: none; // hide scrollbar in firefox
	-ms-overflow-style: none; // hide scroll bar in edge
`

const StyledSearchInputWrapper = styled.div`
	padding: 16px;
`

const StyledFieldListContainer = styled.div`
	display: flex;
	flex: 1;
	max-height: 938px;
	overflow-y: scroll;
`

const TreeContainer = styled.div`
	padding: 14px;
	padding-top: 1px;
	width: 100%;
`

const TreeItemContainer = styled.div`
	display: flex;
	align-items: center;
	transition: opacity 0.2s ease-in-out;
	padding: 8px;
	margin-left: ${(props) => props.$level * 20}px;
	margin-bottom: 2px;
	margin-top: 2px;
	background-color: ${(props) =>
		props.$section
			? props.theme.secondaryColor
			: props.theme.backgroundColor || 'transparent'};
	display: flex;
	align-items: center;
	cursor: pointer;
	border-radius: 6px;
	color: ${(props) => props.theme.fontColorPrimary};
	opacity: ${(props) => props.opacity || 1};
	border: 1px solid transparent;

	&:hover {
		background-color: ${(props) => props.theme.secondaryColor || 'transparent'};
		border: 1px solid ${(props) => props.theme.borderColor};
	}
`

const ExpandButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px 6px;
	border-radius: 4px;
	background: transparent;
	border: none;
	cursor: pointer;
	color: ${(props) => props.theme.fontColorPrimary};
	border: 1px solid transparent;

	&:hover {
		color: ${(props) => props.theme.primaryColor};
		border: 1px solid ${(props) => props.theme.borderColor};
		background: ${(props) => props.theme.backgroundColor};
	}
`

const TreeItemContent = styled.div`
	margin-left: 5px;
	padding-top: 1.5px;
	font-size: ${(props) => (props.$section ? '16px' : '16px')};
	font-weight: ${(props) => (props.$section ? 'bold' : 'normal')};
	color: ${(props) => props.theme.fontColorPrimary};
	display: flex;
	align-items: flex-end;
	gap: 8px;
	font-size: 14px;
	width: 100%;
	justify-content: space-between;

	svg {
		color: ${(props) => props.theme.fontColorPrimary};
	}

	svg.fa-arrows-rotate {
		font-size: 12px;
		left: 6px;
		position: relative;
	}
`
const TreeItemColumn = styled.div`
	flex-direction: column;
	svg {
		margin-left: 8px;
	}
`

const TreeLine = styled.div`
	width: 100%;
`

/**
 * Used to to show sectional (view) tree item content
 * however if its a cloned mobile view we just add those
 * UX icons to make it more clear
 */
const TreeInlineContent = ({ theme, type, title, items, isClone }) => {
	const syncingEnabled = items.type === 'mobile' && isClone
	return (
		<TreeItemContent
			theme={theme}
			$section={type === 'view'}
			title="Mirroring Desktop"
		>
			{title}
			{type === 'view' && syncingEnabled && (
				<TreeItemColumn>
					<Icon
						icon={ThemeIcons.misc.refresh}
						color={theme.fontColorSecondary}
					/>
					<Icon
						icon={ThemeIcons.settings.webScreen}
						size="sm"
						color={theme.fontColorSecondary}
					/>
				</TreeItemColumn>
			)}
		</TreeItemContent>
	)
}

const getNamedPages = (pages = []) => {
	return pages.map((page, index) => ({
		...page,
		name:
			page.name && page.name !== 'New Page' ? page.name : `Page ${index + 1}`,
	}))
}

const Sectional = ({
	items,
	type,
	parentView,
	parentPageId,
	isExpanded,
	onToggleExpand,
	subtype,
	level,
	searchQuery,
	theme,
	isClone,
	name,
	fields,
	onPageSelect,
	onViewSelect,
	...restProps
}) => {
	const icon = isExpanded
		? ThemeIcons.misc.caretDown
		: ThemeIcons.misc.caretRight
	const sublevel = subtype === 'field' ? level + 1 : level
	const isMobileSection = type === 'view' && items.type === 'mobile'

	const handleItemClick = () => {
		if (type === 'view' && onViewSelect) {
			onViewSelect(items.type)
		} else if (type === 'page' && onPageSelect) {
			onPageSelect(items._id, parentView)
		}
	}

	// Map the correct items based on type
	const getItems = () => {
		switch (type) {
			case 'view':
				return getNamedPages(items.pages)
			case 'page':
				return getPopulatedFieldPositions({
					fieldPositions: items.fieldPositions,
					fields,
				})
			default:
				return items
		}
	}

	const filterItems = (items) => {
		if (!searchQuery || type !== 'page') return items

		return items.filter((item) =>
			item.title?.toLowerCase().includes(searchQuery.toLowerCase())
		)
	}

	const displayItems = filterItems(getItems())

	return (
		<TreeLine style={{ paddingBottom: isMobileSection ? 20 : 0 }}>
			<TreeItemContainer
				$level={level}
				theme={theme}
				$section={type === 'view'}
				opacity={isClone && type !== 'view' ? 0.5 : 1}
				onClick={handleItemClick}
			>
				<ExpandButton
					onClick={(e) => {
						e.stopPropagation()
						onToggleExpand()
					}}
					theme={theme}
				>
					<Icon color={theme.fontColorPrimary} icon={icon} size="sm" />
				</ExpandButton>
				<TreeInlineContent
					theme={theme}
					type={type}
					title={name}
					items={items}
					isClone={isClone}
				/>
			</TreeItemContainer>
			{isExpanded &&
				displayItems &&
				displayItems.map((item) => (
					<Branch
						{...restProps}
						fields={fields}
						searchQuery={searchQuery}
						key={item._id}
						data={item}
						level={sublevel}
						type={subtype}
						parentView={parentView}
						parentPageId={parentPageId}
						subtype={subtype}
						theme={theme}
						isClone={isClone}
						onViewSelect={onViewSelect}
						onPageSelect={onPageSelect}
					/>
				))}
		</TreeLine>
	)
}

Sectional.propTypes = {
	items: PropTypes.object.isRequired,
	type: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	subtype: PropTypes.string,
	level: PropTypes.number.isRequired,
	theme: PropTypes.object.isRequired,
	defaultView: PropTypes.string,
	isExpanded: PropTypes.bool.isRequired,
	onToggleExpand: PropTypes.func.isRequired,
	fieldOptions: PropTypes.array,
	fields: PropTypes.array,
	searchQuery: PropTypes.string,
	activePageId: PropTypes.string,
	activeFieldId: PropTypes.string,
	activeFieldFieldId: PropTypes.string,
	selectedView: PropTypes.string,
	onViewSelect: PropTypes.func,
	onPageSelect: PropTypes.func,
	hasMobileView: PropTypes.bool,
}

const Branch = ({
	data,
	level,
	type,
	parentView,
	parentPageId,
	onFieldSelect,
	selectedView,
	theme,
	isClone,
	fieldOptions,
	activePageId,
	activeFieldId,
	activeFieldFieldId,
	hasMobileView,
	...props
}) => {
	const [expanded, setExpanded] = useState(
		type === 'view' ? data.type === selectedView : true
	)

	const handleToggle = () => {
		setExpanded(!expanded)
	}

	const getSubtype = () => {
		if (type === 'view') return 'page'
		if (type === 'page') return 'field'
		return null
	}

	if (type === 'field') {
		/** filter(Boolean) is used to remove any falsy values (e.g., null, undefined) from fieldOptions, ensuring that only valid option objects are processed in the find() call below.*/
		const icon = fieldOptions
			?.filter(Boolean)
			.find((fo) => fo?.type === data?.type)?.icon

		return (
			<FieldPosition
				fieldPosition={data}
				level={level}
				icon={icon}
				activeFieldId={activeFieldId}
				activeFieldFieldId={activeFieldFieldId}
				onFieldSelect={(
					field,
					identifier,
					_id,
					rowId,
					column,
					columnIdentifier
				) => {
					onFieldSelect(
						field,
						identifier,
						_id,
						rowId,
						column,
						columnIdentifier,
						parentView,
						parentPageId
					)
				}}
				isClone={isClone}
				hasMobileView={hasMobileView}
			/>
		)
	}

	// Otherwise, keep going; pass down next parent context
	let nextParentView = parentView
	let nextParentPageId = parentPageId

	// If at "view" level, the new parentView is data.type
	if (type === 'view') {
		nextParentView = data.type
	}
	// If at "page" level, the new parentPageId is data._id
	if (type === 'page') {
		nextParentPageId = data._id
	}

	const typeLabel = data.type
		? data.type.charAt(0).toUpperCase() + data.type.slice(1)
		: data.type

	return (
		<Sectional
			{...props}
			activeFieldId={activeFieldId}
			activeFieldFieldId={activeFieldFieldId}
			fieldOptions={fieldOptions}
			onFieldSelect={onFieldSelect}
			name={type === 'view' ? typeLabel : data.name}
			items={data}
			type={type}
			level={level}
			isExpanded={expanded}
			onToggleExpand={handleToggle}
			parentView={nextParentView}
			parentPageId={nextParentPageId}
			theme={theme}
			subtype={getSubtype()}
			selectedView={selectedView}
			isClone={isClone}
			hasMobileView={hasMobileView}
		/>
	)
}

Branch.propTypes = {
	data: PropTypes.object.isRequired,
	level: PropTypes.number.isRequired,
	type: PropTypes.string.isRequired,
	theme: PropTypes.object,
	defaultView: PropTypes.string,
	selectedView: PropTypes.string,
	activePageId: PropTypes.string,
	activeFieldId: PropTypes.string,
	activeFieldFieldId: PropTypes.string,
	onViewSelect: PropTypes.func,
	onPageSelect: PropTypes.func,
	onFieldSelect: PropTypes.func,
	fieldOptions: PropTypes.array,
	fields: PropTypes.array,
	searchQuery: PropTypes.string,
	isClone: PropTypes.bool,
	hasMobileView: PropTypes.bool,
}

const Branches = ({
	views,
	theme,
	defaultView,
	selectedView,
	activePageId,
	onViewSelect,
	onPageSelect,
	fieldOptions,
	fields,
	searchQuery,
	activeFieldId,
	activeFieldFieldId,
	onFieldSelect,
	hasMobileView,
}) => {
	if (!views) return null
	return views.map((view) => (
		<div key={view._id}>
			<Branch
				data={view}
				level={0}
				type="view"
				isClone={view.clone}
				theme={theme}
				defaultView={defaultView}
				selectedView={selectedView}
				onViewSelect={onViewSelect}
				onPageSelect={onPageSelect}
				activePageId={activePageId}
				fieldOptions={fieldOptions}
				fields={fields}
				searchQuery={searchQuery}
				activeFieldId={activeFieldId}
				activeFieldFieldId={activeFieldFieldId}
				onFieldSelect={onFieldSelect}
				hasMobileView={hasMobileView}
			/>
		</div>
	))
}

Branches.propTypes = {
	views: PropTypes.array.isRequired,
	theme: PropTypes.object.isRequired,
	defaultView: PropTypes.string,
	selectedView: PropTypes.string,
	onViewSelect: PropTypes.func,
	fieldOptions: PropTypes.array.isRequired,
	fields: PropTypes.array.isRequired,
	searchQuery: PropTypes.string,
	activeFieldId: PropTypes.string,
	activeFieldFieldId: PropTypes.string,
	onFieldSelect: PropTypes.func,
	activePageId: PropTypes.string,
	onPageSelect: PropTypes.func,
	hasMobileView: PropTypes.bool,
}

/**
 * Tree
 *
 * Three Levels: views → pages → fields:
 *
 * Tree
 * └─ Branches
 *     └─ Branch (View)
 *         └─ Sectional (Pages)
 *             └─ Branch (Page)
 *                 └─ Sectional (Fields)
 *                     └─ FieldPosition
 *
 * Flow:
 * 1. Tree manages search and view selection state
 * 2. Branch handles expand/collapse
 * 3. Sectional renders items and handles filtering
 */
const Tree = ({
	theme,
	views,
	view,
	fieldOptions,
	fields,
	activeFieldId,
	activeField,
	activePageId,
	onFieldSelect,
	onPageSelect,
	onViewSelect,
	hasMobileView,
}) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedView, setSelectedView] = useState(view)

	useEffect(() => {
		setSelectedView(view)
	}, [view])

	const handleViewSelect = useCallback(
		(viewType) => {
			onFieldSelect(null, null)
			onViewSelect(viewType)
			setSelectedView(viewType)
		},
		[onFieldSelect, onViewSelect]
	)

	const handlePageSelect = useCallback(
		(pageId, targetView) => {
			if (targetView && targetView !== selectedView) {
				onViewSelect(targetView)
			}

			if (pageId !== activePageId) {
				onPageSelect(pageId)
			}
		},
		[onViewSelect, onPageSelect, selectedView, activePageId]
	)

	const handleFieldSelect = useCallback(
		(
			field,
			identifier,
			_id,
			rowId,
			column,
			columnIdentifier,
			targetView,
			targetPageId
		) => {
			handlePageSelect(targetPageId, targetView)
			onFieldSelect(targetPageId, field, identifier, _id, rowId, column, columnIdentifier)
		},
		[handleViewSelect, handlePageSelect, onFieldSelect]
	)

	const handleSearchQueryChange = React.useCallback((value) => {
		const nextSearchQuery = value.toLowerCase()
		setSearchQuery(nextSearchQuery)
	}, [])

	const activeFieldFieldId = activeField?.field

	return (
		<StyledSearchableFieldListWrapper>
			<StyledSearchInputWrapper>
				<InputSettingsField
					onChangeText={handleSearchQueryChange}
					addonBefore={
						<Icon
							color={theme.fontColorPrimary}
							icon={ThemeIcons.misc.search}
							size="sm"
						/>
					}
				/>
			</StyledSearchInputWrapper>

			<StyledFieldListContainer>
				<TreeContainer>
					<Branches
						views={views}
						fields={fields}
						theme={theme}
						defaultView={view}
						selectedView={selectedView}
						activePageId={activePageId}
						activeFieldId={activeFieldId}
						activeFieldFieldId={activeFieldFieldId}
						onPageSelect={handlePageSelect}
						onFieldSelect={handleFieldSelect}
						onViewSelect={handleViewSelect}
						fieldOptions={fieldOptions}
						searchQuery={searchQuery}
						hasMobileView={hasMobileView}
					/>
				</TreeContainer>
			</StyledFieldListContainer>
		</StyledSearchableFieldListWrapper>
	)
}

const getPopulatedFieldPositions = createSelector(
	[(state) => state.fieldPositions, (state) => state.fields],
	(fieldPositions = [], fields = []) => {
		return fieldPositions.map((p) => {
			const matchingField = fields.find((f) => f._id === p.field)
			const nextField = {
				...p,

				// Used purely for visual display of fields in Tree
				title: matchingField?.title || p?.type,
				value: matchingField?.value,
			}

			if (p.type === FieldTypes.multiSelect) {
				nextField.options = matchingField?.options
			}

			return nextField
		})
	}
)

Tree.propTypes = {
	theme: PropTypes.object.isRequired,
	views: PropTypes.array.isRequired,
	view: PropTypes.string,
	fieldOptions: PropTypes.array.isRequired,
	fields: PropTypes.array.isRequired,
	activeFieldId: PropTypes.string,
	onFieldSelect: PropTypes.func,
	activePageId: PropTypes.string,
	onViewSelect: PropTypes.func,
	onPageSelect: PropTypes.func,
	hasMobileView: PropTypes.bool,
}

export default Tree
